import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Nav, NavItem, NavLink, TabContent, TabPane, Button, Badge, Table, Progress, Card, CardBody, Row, Col } from 'reactstrap';
import classnames from 'classnames';
import moment from 'moment';
import { Modal, ModalBody, ModalHeader, Form, FormGroup, Label, Input } from 'reactstrap';
import {
  getSubscriptionPayments,
  getSubscriptionActivity,
  getSubscriptionUsageHistory,
  applySubscriptionCredit,
  cancelSubscription,
  reactivateSubscription
} from '../../../store/Admin';
import './SubDetailsSidebar.css';

const SubDetailsSidebar = ({ isOpen, onClose, subscription, onRefresh }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState({});
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [modalDroppedAlert, setModalDroppedAlert] = useState(false);

  const {
    subscriptionPayments = [],
    subscriptionActivity = [],
    subscriptionUsageHistory = [],
    loadingPayments,
    loadingActivity,
    loadingUsageHistory
  } = useSelector((state) => state.Admin);

  useEffect(() => {
    if (subscription && subscription._id && isOpen) {
      loadTabData(activeTab);
    }
  }, [subscription, isOpen]);

  useEffect(() => {
    if (subscription && subscription._id && isOpen) {
      loadTabData(activeTab);
    }
  }, [activeTab]);

  const loadTabData = (tab) => {
    if (!subscription || !subscription._id) return;

    switch (tab) {
      case 'payments':
        dispatch(getSubscriptionPayments({ id: subscription._id }));
        break;
      case 'usage':
        dispatch(getSubscriptionUsageHistory({ id: subscription._id }));
        break;
      case 'activity':
        dispatch(getSubscriptionActivity({ id: subscription._id }));
        break;
      default:
        break;
    }
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleApplyCredit = async () => {
    if (!creditAmount || creditAmount <= 0) {
      setModalDroppedAlert(true);
      return;
    }

    await dispatch(applySubscriptionCredit({
      id: subscription._id,
      amount: parseFloat(creditAmount), // Backend expects dollars
      description: creditReason || 'Admin credit adjustment'
    }));

    setShowCreditModal(false);
    setCreditAmount('');
    setCreditReason('');

    // Force refresh subscription data
    if (onRefresh) {
      await onRefresh();
    }

    // Also refresh the current subscription details
    loadTabData('overview');
  };

  const handleCancelSubscription = async () => {
    if (window.confirm('Are you sure you want to cancel this subscription at the end of the billing period?')) {
      await dispatch(cancelSubscription({ id: subscription._id }));
      if (onRefresh) {
        await onRefresh();
      }
      // Refresh current tab data
      loadTabData(activeTab);
    }
  };

  const handleReactivateSubscription = async () => {
    if (window.confirm('Are you sure you want to reactivate this subscription?')) {
      await dispatch(reactivateSubscription({ id: subscription._id }));
      if (onRefresh) {
        await onRefresh();
      }
      // Refresh current tab data
      loadTabData(activeTab);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      free: 'info',
      canceled: 'danger',
      pastDue: 'warning',
      unpaid: 'danger'
    };
    return colors[status] || 'secondary';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getCustomerName = () => {
    if (!subscription) return 'Unknown';

    const workspace = subscription.workspaceId;
    if (!workspace) return 'Unknown';

    const owner = workspace.ownerId;
    if (!owner) return workspace.name || 'Unknown';

    const firstName = owner.firstName || '';
    const lastName = owner.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || owner.email || 'Unknown';
  };

  const getCustomerEmail = () => {
    if (!subscription) return '';
    const workspace = subscription.workspaceId;
    if (!workspace || !workspace.ownerId) return '';
    return workspace.ownerId.email || '';
  };

  const renderOverviewTab = () => (
    <div className="tab-content-wrapper">
      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">Subscription Details</h6>
          <Row>
            <Col md={6}>
              <div className="detail-item">
                <span className="detail-label">Customer:</span>
                <strong>{getCustomerName()}</strong>
              </div>
              <div className="detail-item">
                <span className="detail-label">Email:</span>
                <span>{getCustomerEmail()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Workspace:</span>
                <span>{subscription?.workspaceId?.name || 'N/A'}</span>
              </div>
            </Col>
            <Col md={6}>
              <div className="detail-item">
                <span className="detail-label">Plan:</span>
                <strong>{subscription?.planName || 'N/A'}</strong>
              </div>
              <div className="detail-item">
                <span className="detail-label">Amount:</span>
                <span>{formatCurrency(subscription?.planId?.price || 0)}/month</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <Badge color={getStatusColor(subscription?.status)}>
                  {subscription?.status || 'Unknown'}
                </Badge>
                {subscription?.cancelAtPeriodEnd && (
                  <Badge color="warning" className="ml-2">
                    Cancels at period end
                  </Badge>
                )}
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">Billing Information</h6>
          <Row>
            <Col md={6}>
              <div className="detail-item">
                <span className="detail-label">Current Period Start:</span>
                <span>{subscription?.currentPeriodStart ?
                  moment(subscription.currentPeriodStart).format('MMM DD, YYYY') : 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Current Period End:</span>
                <span>{subscription?.currentPeriodEnd ?
                  moment(subscription.currentPeriodEnd).format('MMM DD, YYYY') : 'N/A'}</span>
              </div>
            </Col>
            <Col md={6}>
              <div className="detail-item">
                <span className="detail-label">Credit Balance:</span>
                <strong className="text-success">
                  {formatCurrency((subscription?.creditBalance || subscription?.creditsBalance || 0) / 100)}
                </strong>

              </div>
              <div className="detail-item">
                <span className="detail-label">Last Credit Applied:</span>
                <span>{subscription?.lastCredited ?
                  formatCurrency(subscription.lastCredited / 100) : 'Never'}</span>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardBody>
          <h6 className="mb-3">Current Usage</h6>
          <div className="usage-metrics">
            <div className="usage-item mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span>Emails Sent</span>
                <span>{subscription?.usageMetrics?.currentPeriodUsage?.emails || 0} / {subscription?.planId?.features?.emails || 'Unlimited'}</span>
              </div>
              {subscription?.planId?.features?.emails !== 'unlimited' && (
                <Progress
                  value={(subscription?.usageMetrics?.currentPeriodUsage?.emails || 0) / subscription?.planId?.features?.emails * 100}
                  color="primary"
                />
              )}
            </div>
            <div className="usage-item mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span>Contacts</span>
                <span>{subscription?.usageMetrics?.currentPeriodUsage?.contacts || 0} / {subscription?.planId?.features?.contacts || 'Unlimited'}</span>
              </div>
              {subscription?.planId?.features?.contacts !== 'unlimited' && (
                <Progress
                  value={(subscription?.usageMetrics?.currentPeriodUsage?.contacts || 0) / subscription?.planId?.features?.contacts * 100}
                  color="info"
                />
              )}
            </div>
            <div className="usage-item mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span>Calling Minutes</span>
                <span>{subscription?.usageMetrics?.currentPeriodUsage?.callingMinutes || 0} / {subscription?.planId?.features?.callingMinutes || 'Unlimited'}</span>
              </div>
              {subscription?.planId?.features?.callingMinutes !== 'unlimited' && (
                <Progress
                  value={(subscription?.usageMetrics?.currentPeriodUsage?.callingMinutes || 0) / subscription?.planId?.features?.callingMinutes * 100}
                  color="warning"
                />
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="action-buttons">
        {subscription?.status === 'active' && !subscription?.cancelAtPeriodEnd && (
          <Button color="danger" onClick={handleCancelSubscription}>
            Cancel Subscription
          </Button>
        )}
        {(subscription?.status === 'canceled' || subscription?.cancelAtPeriodEnd) && (
          <Button color="success" onClick={handleReactivateSubscription}>
            Reactivate Subscription
          </Button>
        )}
      </div>
    </div>
  );

  const renderPaymentsTab = () => (
    <div className="tab-content-wrapper">
      {loadingPayments ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <Table responsive hover className="mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {subscriptionPayments && subscriptionPayments.length > 0 ? (
              subscriptionPayments.map((payment, index) => (
                <tr key={index}>
                  <td>{moment(payment.created * 1000).format('MMM DD, YYYY')}</td>
                  <td>{payment.description || 'Subscription payment'}</td>
                  <td>{formatCurrency(payment.amount / 100)}</td>
                  <td>
                    <Badge color={payment.status === 'succeeded' ? 'success' : 'danger'}>
                      {payment.status}
                    </Badge>
                  </td>
                  <td>
                    {payment.invoice && (
                      <Button color="link" size="sm">
                        <i className="fa fa-download"></i> Download
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted">No payment history found</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </div>
  );

  const renderUsageTab = () => (
    <div className="tab-content-wrapper">
      {loadingUsageHistory ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-3">Usage Limits</h6>
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Used</th>
                    <th>Limit</th>
                    <th>Overage</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Emails</td>
                    <td>{subscription?.usageMetrics?.currentPeriodUsage?.emails || 0}</td>
                    <td>{subscription?.planId?.features?.emails || 'Unlimited'}</td>
                    <td className="text-danger">
                      {subscription?.usageMetrics?.overage?.emails || 0}
                    </td>
                  </tr>
                  <tr>
                    <td>Contacts</td>
                    <td>{subscription?.usageMetrics?.currentPeriodUsage?.contacts || 0}</td>
                    <td>{subscription?.planId?.features?.contacts || 'Unlimited'}</td>
                    <td className="text-danger">
                      {subscription?.usageMetrics?.overage?.contacts || 0}
                    </td>
                  </tr>
                  <tr>
                    <td>Calling Minutes</td>
                    <td>{subscription?.usageMetrics?.currentPeriodUsage?.callingMinutes || 0}</td>
                    <td>{subscription?.planId?.features?.callingMinutes || 'Unlimited'}</td>
                    <td className="text-danger">
                      {subscription?.usageMetrics?.overage?.callingMinutes || 0}
                    </td>
                  </tr>
                </tbody>
              </Table>
              {subscription?.usageMetrics?.overage?.charges > 0 && (
                <div className="alert alert-warning mt-3">
                  <strong>Overage Charges:</strong> {formatCurrency(subscription.usageMetrics.overage.charges)}
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6 className="mb-3">Historical Usage</h6>
              {subscriptionUsageHistory && subscriptionUsageHistory.length > 0 ? (
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Emails</th>
                      <th>Contacts</th>
                      <th>Minutes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptionUsageHistory.map((usage, index) => (
                      <tr key={index}>
                        <td>{moment(usage.period).format('MMM YYYY')}</td>
                        <td>{usage.emails || 0}</td>
                        <td>{usage.contacts || 0}</td>
                        <td>{usage.callingMinutes || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted text-center">No historical usage data available</p>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );

  const renderActivityTab = () => (
    <div className="tab-content-wrapper">
      {loadingActivity ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="activity-timeline">
          {subscriptionActivity && subscriptionActivity.length > 0 ? (
            subscriptionActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon">
                  <i className={`fa ${getActivityIcon(activity.type)}`}></i>
                </div>
                <div className="activity-content">
                  <div className="activity-header">
                    <strong>{activity.description}</strong>
                    <small className="text-muted">
                      {moment(activity.createdAt).format('MMM DD, YYYY HH:mm')}
                    </small>
                  </div>
                  {activity.details && (
                    <div className="activity-details text-muted">
                      {activity.details}
                    </div>
                  )}
                  {activity.performedBy && (
                    <div className="activity-user">
                      <small>By: {activity.performedBy.firstName} {activity.performedBy.lastName}</small>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted text-center">No activity history found</p>
          )}
        </div>
      )}
    </div>
  );

  const getActivityIcon = (type) => {
    const icons = {
      'subscription_created': 'fa-plus-circle',
      'subscription_upgraded': 'fa-arrow-up',
      'subscription_downgraded': 'fa-arrow-down',
      'subscription_canceled': 'fa-times-circle',
      'subscription_reactivated': 'fa-check-circle',
      'credit_applied': 'fa-dollar-sign',
      'payment_succeeded': 'fa-check',
      'payment_failed': 'fa-exclamation-triangle',
      'plan_changed': 'fa-exchange-alt'
    };
    return icons[type] || 'fa-info-circle';
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} modalClassName='right right-without-bg' backdrop={false} className={`main-modal right right-side-modal style-three`}>
        <ModalHeader toggle={onClose}>
          <h3>Subscription Details
          </h3>
          <p className="text-muted mb-0">
            {getCustomerName()} - {subscription?.planName}
          </p>
        </ModalHeader>
        <ModalBody>
          <div className='filters-modal-content'>
            <div className='function-box'>
              <div className="sidebar-body">
                <div className='tabs-area mt-4'>
                  {/* Navigation Tabs */}
                  <div className='tab-header'>
                    {/* Navigation Tabs */}
                    <Nav tabs>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'overview' })}
                          onClick={() => toggle('overview')}
                        >
                          Overview
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'payments' })}
                          onClick={() => toggle('payments')}
                        >
                          Payments
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'usage' })}
                          onClick={() => toggle('usage')}
                        >
                          Usage
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === 'activity' })}
                          onClick={() => toggle('activity')}
                        >
                          Activity
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </div>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="overview">
                    {renderOverviewTab()}
                  </TabPane>
                  <TabPane tabId="payments">
                    {renderPaymentsTab()}
                  </TabPane>
                  <TabPane tabId="usage">
                    {renderUsageTab()}
                  </TabPane>
                  <TabPane tabId="activity">
                    {renderActivityTab()}
                  </TabPane>
                </TabContent>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
      {/* <div className="sidebar-overlay" onClick={onClose} /> */}
      <Modal isOpen={showCreditModal} toggle={() => setShowCreditModal(false)}>
        <ModalHeader toggle={() => setShowCreditModal(false)}>
          Apply Credit
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Add Credit Count</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="Enter amount in dollars"
              />
            </FormGroup>
            <FormGroup>
              <Label>Reason (Optional)</Label>
              <Input
                type="textarea"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder="Enter reason for credit"
              />
            </FormGroup>
            <div className="d-flex justify-content-end">
              <Button color="secondary" className="mr-2" onClick={() => setShowCreditModal(false)}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleApplyCredit}>
                Apply Credit
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* --------------- Altert Dropped--------------- */}
         <Modal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
            <ModalHeader toggle={() => setModalDroppedAlert(false)}></ModalHeader>
            <ModalBody>
               <div className='add-content'>
                  <h2>Please enter a valid credit amount</h2>
                  <div className='templates-inner'>
                     <div className='btn-group'>
                        <button className='btn-style-new' onClick={() => setModalDroppedAlert(false)}>Ok</button>
                     </div>
                  </div>
               </div>
            </ModalBody>
         </Modal>
    </>
  );
};

export default SubDetailsSidebar;